<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import type { UseKeyEvents } from '../../composables/useKeyEvents'
import type { KeyEventAccount } from '../../models/conversion.types'
import { eventLabel } from '../../service/keyEvents'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Modal, MultiSelect, PlatformLogo, Select, Spinner } from '@/shared/ui'

const props = defineProps<{ account: KeyEventAccount; controller: UseKeyEvents }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const open = ref(true)
const available = ref<string[]>([])
const selected = ref([...props.account.selection.events])
const primary = ref<string | null>(props.account.selection.primary)
const options = computed(() =>
  available.value.map((value) => ({ value, label: eventLabel(value) })),
)
const selectedOptions = computed(() =>
  selected.value.map((value) => ({ value, label: eventLabel(value) })),
)

async function load(): Promise<void> {
  const result = await props.controller.fetchLive(props.account.accountId)
  if (result === null) return
  available.value = result
  selected.value = selected.value.filter((event) => result.includes(event))
}

async function save(): Promise<void> {
  const saved = await props.controller.save(props.account.accountId, {
    events: selected.value,
    primary: primary.value,
  })
  if (saved) emit('close')
}

watch(selected, (events) => {
  if (primary.value && !events.includes(primary.value)) primary.value = null
})
onMounted(() => void load())
</script>

<template>
  <Modal v-model="open" size="lg" @close="emit('close')">
    <template #title>
      <span class="key-events-modal__title inline-flex items-center gap-sm">
        <PlatformLogo :platform="controller.descriptor.logo" :size="20" />
        {{ t('workspaceDefinitions.conversions.keyEvents.modalTitle') }}
      </span>
    </template>
    <template #body>
      <div class="key-events-modal__body grid gap-lg">
        <p>
          <strong>{{ t('workspaceDefinitions.conversions.keyEvents.account') }}</strong>
          {{ account.label }}
        </p>
        <div
          v-if="controller.liveLoading.value"
          class="key-events-modal__center flex justify-center p-lg"
        >
          <Spinner />
        </div>
        <Alert v-else-if="controller.error.value" tone="danger">
          <span>{{ t('workspaceDefinitions.conversions.keyEvents.loadError') }}</span>
          <Button variant="outline" size="sm" @click="load">
            {{ t('workspaceDefinitions.conversions.keyEvents.retry') }}
          </Button>
        </Alert>
        <Alert v-else-if="!available.length" tone="info">
          {{ t('workspaceDefinitions.conversions.keyEvents.noEvents') }}
        </Alert>
        <template v-else>
          <MultiSelect
            v-model="selected"
            :options="options"
            :label="t('workspaceDefinitions.conversions.keyEvents.events')"
            :placeholder="t('workspaceDefinitions.conversions.keyEvents.eventPlaceholder')"
            searchable
            :max="10"
            :max-visible-chips="5"
          />
          <Select
            v-if="selected.length"
            v-model="primary"
            :options="selectedOptions"
            :label="t('workspaceDefinitions.conversions.keyEvents.primary')"
            :placeholder="t('workspaceDefinitions.conversions.keyEvents.conversionPlaceholder')"
            searchable
            clearable
          />
        </template>
        <Alert
          v-if="controller.error.value && !controller.liveLoading.value && available.length"
          tone="danger"
        >
          {{ t('workspaceDefinitions.common.saveError') }}
        </Alert>
      </div>
    </template>
    <template #buttons>
      <Button
        variant="outline"
        size="sm"
        :disabled="controller.pending.value"
        @click="emit('close')"
      >
        {{ t('workspaceDefinitions.common.cancel') }}
      </Button>
      <Button
        size="sm"
        :loading="controller.pending.value"
        :disabled="!selected.length || controller.liveLoading.value"
        data-testid="key-events-save"
        @click="save"
      >
        {{ t('workspaceDefinitions.common.save') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.key-events-modal__body p {
  margin: 0;
  font-size: 0.875rem;
}
</style>
